// Thin fetch wrapper. Every request hits the Vite proxy, which forwards
// to FastAPI on :8000. No axios — the app is small enough not to need it.

import type {
  Approval,
  FeedbackEvent,
  FeedbackRating,
  GenerateRequest,
  JobSnapshot,
  KeyStatus,
  KeysResponse,
  PostDetail,
  PostSummary,
  SettingsResponse,
} from "./types";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  if (response.status === 401) {
    // Session expired or never logged in — kick to /login and stop.
    // Wrap in a small delay so a rapid 401 storm doesn't race the
    // browser navigation.
    if (
      typeof window !== "undefined" &&
      !window.location.pathname.startsWith("/login")
    ) {
      const redirect = encodeURIComponent(
        window.location.pathname + window.location.search,
      );
      window.location.replace(`/login?next=${redirect}`);
    }
    throw new Error("401 Unauthorized");
  }
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(
      `${response.status} ${response.statusText} at ${path}${
        text ? `: ${text}` : ""
      }`,
    );
  }
  return (await response.json()) as T;
}

export interface AuthUser {
  username: string;
  role: string;
}

export const authApi = {
  login: (username: string, password: string): Promise<AuthUser> =>
    request<AuthUser>(`/api/auth/login`, {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),
  logout: (): Promise<{ ok: boolean }> =>
    request<{ ok: boolean }>(`/api/auth/logout`, { method: "POST" }),
  me: (): Promise<AuthUser> => request<AuthUser>(`/api/auth/me`),
};

export const api = {
  posts: {
    list: (params: {
      date?: string;
      category?: string;
      status?: string;
    } = {}): Promise<PostSummary[]> => {
      const qs = new URLSearchParams();
      if (params.date) qs.set("date", params.date);
      if (params.category) qs.set("category", params.category);
      if (params.status) qs.set("status", params.status);
      const suffix = qs.toString() ? `?${qs.toString()}` : "";
      return request<PostSummary[]>(`/api/posts${suffix}`);
    },
    get: (id: string): Promise<PostDetail> =>
      request<PostDetail>(`/api/posts/${encodeURIComponent(id)}`),
    patch: (
      id: string,
      body: { caption_feed?: string; hashtags?: string[]; source?: string },
    ): Promise<PostDetail> =>
      request<PostDetail>(`/api/posts/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    approve: (
      id: string,
      body: { notes?: string; publish_to_instagram?: boolean } = {},
    ): Promise<Approval> =>
      request<Approval>(`/api/posts/${encodeURIComponent(id)}/approve`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    reject: (id: string, body: { notes?: string } = {}): Promise<Approval> =>
      request<Approval>(`/api/posts/${encodeURIComponent(id)}/reject`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    rate: (
      id: string,
      body: { rating: FeedbackRating; comment?: string },
    ): Promise<FeedbackEvent> =>
      request<FeedbackEvent>(
        `/api/posts/${encodeURIComponent(id)}/feedback`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    feedbackHistory: (id: string): Promise<FeedbackEvent[]> =>
      request<FeedbackEvent[]>(
        `/api/posts/${encodeURIComponent(id)}/feedback`,
      ),
  },
  feedback: {
    recent: (params: { limit?: number; rating?: FeedbackRating } = {}): Promise<
      FeedbackEvent[]
    > => {
      const qs = new URLSearchParams();
      if (params.limit != null) qs.set("limit", String(params.limit));
      if (params.rating) qs.set("rating", params.rating);
      const suffix = qs.toString() ? `?${qs.toString()}` : "";
      return request<FeedbackEvent[]>(`/api/feedback/recent${suffix}`);
    },
  },
  settings: {
    get: (): Promise<SettingsResponse> =>
      request<SettingsResponse>(`/api/settings`),
    getKeys: (): Promise<KeysResponse> => request<KeysResponse>(`/api/settings/keys`),
    putKey: (name: string, value: string): Promise<KeyStatus> =>
      request<KeyStatus>(`/api/settings/keys`, {
        method: "PUT",
        body: JSON.stringify({ name, value }),
      }),
  },
  generate: {
    start: (body: GenerateRequest): Promise<{ job_id: string }> =>
      request<{ job_id: string }>(`/api/generate`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    status: (jobId: string): Promise<JobSnapshot> =>
      request<JobSnapshot>(
        `/api/generate/${encodeURIComponent(jobId)}/status`,
      ),
    listJobs: (): Promise<JobSnapshot[]> =>
      request<JobSnapshot[]>(`/api/generate/jobs`),
    // Legacy SSE endpoint — kept but unused by the UI; the browser uses
    // polling so the flow works through Cloudflare tunnels and proxies.
    streamUrl: (jobId: string): string =>
      `/api/generate/${encodeURIComponent(jobId)}/stream`,
  },
  analytics: {
    scores: (): Promise<
      Array<{
        date: string;
        post_id: string;
        category: string;
        template?: string | null;
        brand_score: number;
      }>
    > => request(`/api/analytics/scores`),
    cadence: (): Promise<{ by_category: Record<string, number>; total: number }> =>
      request(`/api/analytics/cadence`),
  },
};
